import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Input variants using CVA
 *
 * Variants:
 * - default: Standard input styling
 * - error: Red border for validation errors
 * - success: Green border for valid fields
 */
export const inputVariants = cva(
  'flex w-full rounded-lg border bg-[var(--surface-base)] text-[var(--text-primary)] px-3 py-2 text-sm transition-all duration-normal file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[var(--text-tertiary)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border-[var(--border-secondary)] focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500/20',
        error:
          'border-error-500 focus-visible:border-error-500 focus-visible:ring-2 focus-visible:ring-error-500/20',
        success:
          'border-success-500 focus-visible:border-success-500 focus-visible:ring-2 focus-visible:ring-success-500/20',
      },
      inputSize: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-10 px-3 text-sm',
        lg: 'h-12 px-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'md',
    },
  }
);

/**
 * Input Component
 *
 * @param {Object} props
 * @param {'default'|'error'|'success'} [props.variant='default']
 * @param {'sm'|'md'|'lg'} [props.inputSize='md']
 * @param {string} [props.className]
 */
const Input = React.forwardRef(({ className, variant, inputSize, type = 'text', ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(inputVariants({ variant, inputSize }), className)}
      ref={ref}
      {...props}
    />
  );
});

Input.displayName = 'Input';

/**
 * Textarea Component (uses same styling as Input)
 */
export const textareaVariants = cva(
  'flex w-full rounded-lg border bg-[var(--surface-base)] text-[var(--text-primary)] px-3 py-2 text-sm transition-all duration-normal placeholder:text-[var(--text-tertiary)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] resize-y',
  {
    variants: {
      variant: {
        default:
          'border-[var(--border-secondary)] focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500/20',
        error:
          'border-error-500 focus-visible:border-error-500 focus-visible:ring-2 focus-visible:ring-error-500/20',
        success:
          'border-success-500 focus-visible:border-success-500 focus-visible:ring-2 focus-visible:ring-success-500/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Textarea = React.forwardRef(({ className, variant, ...props }, ref) => {
  return (
    <textarea
      className={cn(textareaVariants({ variant }), className)}
      ref={ref}
      {...props}
    />
  );
});

Textarea.displayName = 'Textarea';

/**
 * Label Component
 */
export const labelVariants = cva(
  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
  {
    variants: {
      variant: {
        default: 'text-[var(--text-secondary)]',
        required: 'text-[var(--text-secondary)] after:ml-0.5 after:text-error-500 after:content-["*"]',
        error: 'text-error-600 dark:text-error-400',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Label = React.forwardRef(({ className, variant, ...props }, ref) => (
  <label
    ref={ref}
    className={cn(labelVariants({ variant }), className)}
    {...props}
  />
));

Label.displayName = 'Label';

/**
 * FormGroup Component - Wrapper for form fields
 */
const FormGroup = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('space-y-2', className)} {...props}>
    {children}
  </div>
));

FormGroup.displayName = 'FormGroup';

/**
 * FormMessage Component - For error/help text
 */
const FormMessage = React.forwardRef(({ className, variant = 'default', children, ...props }, ref) => {
  const variants = {
    default: 'text-[var(--text-tertiary)]',
    error: 'text-error-600 dark:text-error-400',
    success: 'text-success-600 dark:text-success-400',
  };

  return (
    <p
      ref={ref}
      className={cn('text-sm', variants[variant], className)}
      {...props}
    >
      {children}
    </p>
  );
});

FormMessage.displayName = 'FormMessage';

export { Input, Textarea, Label, FormGroup, FormMessage };
export default Input;
